import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

interface Role {
  id: number;
  nome: string;
  descricao: string;
}

interface InviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { session } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [formData, setFormData] = useState({
    nome_assessor: '',
    papel_id: 0,
  });
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [error, setError] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');

  useEffect(() => {
    if (isOpen && session?.access_token) {
      setLoadingRoles(true);
      apiFetch(`${API_BASE_URL}/api/v1/roles`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      })
        .then(r => r.json())
        .then(result => {
          if (result.success && result.data) {
            setRoles(result.data);
            if (result.data.length > 0) {
              setFormData(f => ({ ...f, papel_id: result.data[0].id }));
            }
          }
        })
        .catch(err => console.error('Error fetching roles:', err))
        .finally(() => setLoadingRoles(false));
    }
  }, [isOpen, session?.access_token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/convites`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao gerar convite');
      }

      const result = await response.json();
      setGeneratedCode(result.data.codigo);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(generatedCode);
    alert('Código copiado!');
  };

  const handleClose = () => {
    setFormData({ nome_assessor: '', papel_id: roles[0]?.id || 0 });
    setGeneratedCode('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>{generatedCode ? 'Convite Gerado!' : 'Convidar Membro'}</h2>
          <CloseButton onClick={handleClose}>×</CloseButton>
        </ModalHeader>

        {generatedCode ? (
          <SuccessContent>
            <SuccessTitle>Código de Convite</SuccessTitle>
            <CodeDisplay>{generatedCode}</CodeDisplay>
            <AssessorInfo>
              Para <AssessorName>{formData.nome_assessor}</AssessorName> · {roles.find(r => r.id === formData.papel_id)?.nome || ''}
            </AssessorInfo>
            <ValidityInfo>
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Válido por 7 dias
            </ValidityInfo>
            
            <ButtonGroup>
              <CopyButton onClick={handleCopyCode}>
                <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copiar Código
              </CopyButton>
              <Button onClick={handleClose}>
                Fechar
              </Button>
            </ButtonGroup>
          </SuccessContent>
        ) : (
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="nome_assessor">Nome do Membro *</Label>
              <Input
                id="nome_assessor"
                type="text"
                value={formData.nome_assessor}
                onChange={(e) => setFormData({ ...formData, nome_assessor: e.target.value })}
                required
                disabled={loading}
                placeholder="Nome completo"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="papel_id">Papel do Membro *</Label>
              <Select
                id="papel_id"
                value={formData.papel_id}
                onChange={(e) => setFormData({ ...formData, papel_id: Number(e.target.value) })}
                disabled={loading || loadingRoles}
              >
                {loadingRoles && <option value="">Carregando...</option>}
                {!loadingRoles && roles.length === 0 && <option value="">Nenhum papel disponível</option>}
                {roles.map(role => (
                  <option key={role.id} value={role.id}>{role.nome}</option>
                ))}
              </Select>
            </FormGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <ButtonGroup>
              <Button type="button" onClick={handleClose} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} primary>
                {loading && (
                  <SpinIcon width="15" height="15" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.35)" strokeWidth="3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                  </SpinIcon>
                )}
                {loading ? 'Gerando...' : 'Gerar Convite'}
              </Button>
            </ButtonGroup>
          </Form>
        )}
      </ModalContent>
    </Overlay>
  );
};

export default InviteModal;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const Form = styled.form`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 1rem;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &:disabled {
    background-color: #f9fafb;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fee;
  color: #dc2626;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid #fdd;
`;

const SpinIcon = styled.svg`
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Button = styled.button<{ primary?: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.primary ? `
    background-color: var(--accent);
    color: white;

    &:hover:not(:disabled) {
      background-color: var(--accent-dark);
    }
  ` : `
    background-color: white;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background-color: #f9fafb;
    }
  `}

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  ${props => props.primary && `
    &:disabled {
      background-color: var(--accent);
      opacity: 0.65;
    }
  `}

  ${props => !props.primary && `
    &:disabled {
      background-color: white;
      border: 1px solid #d1d5db;
      color: #9ca3af;
      opacity: 1;
    }
  `}
`;

const SuccessContent = styled.div`
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const SuccessTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const CodeDisplay = styled.div`
  background: linear-gradient(135deg, var(--accent) 0%, #10b981 100%);
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  padding: 2rem 3rem;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(var(--accent-rgb), 0.3);
  width: 100%;
  max-width: 400px;
`;

const AssessorInfo = styled.p`
  color: #64748b;
  font-size: 1rem;
  margin: 0;
`;

const AssessorName = styled.strong`
  color: #1e293b;
  font-weight: 600;
`;

const ValidityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;
  
  svg {
    flex-shrink: 0;
  }
`;

const CopyButton = styled(Button)`
  background-color: #10b981;
  color: white;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    flex-shrink: 0;
  }

  &:hover:not(:disabled) {
    background-color: #059669;
  }
`;
